import React from 'react';
import { csvCell } from '../lib/csvCell';
import SearchableSelect from './shared/SearchableSelect';
import UnifiedApplicantFilterBar from './shared/UnifiedApplicantFilterBar';
import ReactDOM from 'react-dom';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { useModalLayer } from '../lib/modalStack';
import { navigate } from '../lib/router';
import { notifyToast } from '../lib/toastStore';
import { confirmDialog, promptDialog } from '../lib/confirmStore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { LogOut, LayoutDashboard, Layers, Users, Search, Plus, X, User, Receipt, List, Eye, ChevronLeft, ChevronRight, RefreshCw, PanelLeftClose, PanelLeftOpen, Upload, CheckCircle, Check, Download, RotateCcw, Send, UserPlus, FileText, File, Archive, Trash2, AlertTriangle, Mail, TicketCheck, Bell, BellOff, BellRing, ChevronDown, Settings, UserCircle, Briefcase, Building, Building2, Tag, Sparkles, CheckCircle2, UserCheck, SlidersHorizontal } from 'lucide-react';
import { Application, ApplicationStatus, UserProfile } from '../types';
import { KpiCard, StatusBadge } from './shared/DashboardPrimitives';
import MasterPipeline, { MASTER_STAGES } from './MasterPipeline';
import { getPipelineStages, stageAtIndex, stageLabel } from '../lib/programPipeline';
import type { ProgramConfig } from '../lib/programConfig';
import ApplicationWizard from './ApplicationWizard';
import { usePrograms } from '../hooks/usePrograms';
import { useApplications } from './admin/hooks/useApplications';
import { isAdminTabId } from '../lib/adminTabs';
import { useEvents } from './admin/hooks/useEvents';
import { useBatches } from './admin/hooks/useBatches';
import { useUsers } from './admin/hooks/useUsers';
import BatchCenter from './admin/BatchCenter';
import UsersTab from './admin/UsersTab';
import ApplicantDetail from './admin/ApplicantDetail';
import EventsManager from './admin/EventsManager';
import EmailTemplateManager from './admin/EmailTemplateManager';

import DocumentTemplateManager from './admin/DocumentTemplateManager';
import CsvImportModal from './admin/CsvImportModal';
import TicketsPage from './admin/TicketsPage';
import ProfileModal from './admin/ProfileModal';
import NotificationBanner from './shared/NotificationBanner';
import NotificationCenter from './shared/NotificationCenter';
import TaskNotificationCenter from './shared/TaskNotificationCenter';
import JobsManager from './admin/JobsManager';
import EmployersManager from './admin/EmployersManager';
import AdminInvitationsManager from './admin/AdminInvitationsManager';
import AdminOffersManager from './admin/AdminOffersManager';
import AdminJobsTabBar from './admin/AdminJobsTabBar';
import { hasModuleAccess, moduleAccessLevel, canSeeWorkspaceSettings as canSeeWorkspaceSettingsGate } from '../lib/permissions';
import AdminReports from './admin/AdminReports';
import PendingApprovalsManager from './admin/PendingApprovalsManager';
import AdminSettings from './admin/AdminSettings';
import MyEventsView from './shared/MyEventsView';
import { BulkJobsDashboard } from './admin/BulkJobsDashboard';
import { pageSelectionShare, togglePageSelection } from './admin/batchSelection';
import ReferrersManager from './admin/ReferrersManager';
import SponsorsManager from './admin/SponsorsManager';
import WorkspaceManagement from './admin/WorkspaceManagement';
import AiSuggestionsManager from './admin/AiSuggestionsManager';
import DashboardShell from './shared/DashboardShell';
import OrgAdminArea from './admin/OrgAdminArea';
import FinanceTab from './admin/FinanceTab';
import WorkspaceTeamAssignModal from './admin/WorkspaceTeamAssignModal';
import { useWorkspaces } from './shared/WorkspaceProvider';
import WorkspaceSettings from './shared/WorkspaceSettings';
import { isApplicationActive } from '../lib/constants';
import ProgramCardHub from './admin/ProgramCardHub';
import ArchivedApplications from './admin/ArchivedApplications';
import { WorkflowPage } from './admin/workflow/WorkflowPage';
import { DocumentsCenter, AdminDocumentItem } from './admin/DocumentsCenter';
import EsignaturesPage from './admin/EsignaturesPage';
import { DocumentPreviewModal } from './admin/DocumentPreviewModal';
import { AutomationManager } from './admin/automation/AutomationManager';
// New shared components
import { AppShell, TopNav, Sidebar, Button, Input, Select, DataTable, Card, CardList, Avatar, Modal, Skeleton, EmptyState, Breadcrumb, Tag, Badge } from './shared';

interface AdminDashboardProps {
  onLogout: () => void;
  adminEmail: string;
  userProfile: UserProfile;
  /** Platform Super Administrator — sees the Workspace Management console. */
  isPlatformAdmin?: boolean;
}

const getSubStatus = (app: any) => app ? (app as any).submissionStatus || app.status || 'Draft' : 'Draft';

/* ── Notification Toggle (compact, inline) ── */
function NotificationToggleInline() {
  const [notifState, setNotifState] = React.useState<'loading' | 'granted' | 'denied' | 'default'>('loading');
  React.useEffect(() => {
    if (!('Notification' in window)) { setNotifState('denied'); return; }
    setNotifState(Notification.permission as any);
  }, []);
  const handleToggle = async () => {
    if (notifState === 'denied') { notifyToast('warning', 'Notifications are blocked for this site. Enable them in your browser settings.'); return; }
    if (notifState === 'granted') return;
    setNotifState('loading');
    try {
      const { requestNotificationPermission } = await import('../lib/messaging');
      const token = await requestNotificationPermission();
      setNotifState(token ? 'granted' : 'denied');
    } catch { setNotifState('denied'); }
  };
  return (
    <button onClick={handleToggle} disabled={notifState === 'loading'} className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer">
      {notifState === 'granted' ? <BellRing className="h-4 w-4 text-emerald-500" /> : notifState === 'denied' ? <BellOff className="h-4 w-4 text-slate-300" /> : <Bell className="h-4 w-4 text-slate-400" />}
      {notifState === 'granted' ? 'Notifications On' : notifState === 'denied' ? 'Notifications Blocked' : 'Enable Notifications'}
    </button>
  );
}

// Public Batch ID first, then the name — the one label format for
// every batch dropdown/selector in the portal (D-5).
function batchPublicLabel(b: any): string {
  const displayId = b?.batchId?.startsWith('BAT-')
    ? b.batchId
    : b?.numericId
      ? `BAT-${b.numericId}`
      : b?.batchId || '—';
  const rawName = typeof b?.name === 'string' ? b.name : '';
  return rawName && !rawName.startsWith('BAT-') ? `${displayId} - ${rawName}` : displayId;
}

export default function AdminDashboard({ onLogout, adminEmail, userProfile, isPlatformAdmin }: AdminDashboardProps) {
  const { activeWorkspace, platformAdmin, loading: workspacesLoading } = useWorkspaces();
  const [applicantListCollapsed, setApplicantListCollapsed] = React.useState(false);
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>(['firstName', 'lastName', 'email', 'program', 'masterPhase', 'status', 'tags', 'partner']);
  const [showColumnPicker, setShowColumnPicker] = React.useState(false);
  const columnPickerWrapperRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!showColumnPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPickerWrapperRef.current && !columnPickerWrapperRef.current.contains(e.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showColumnPicker]);
  const [showMoreBulkActions, setShowMoreBulkActions] = React.useState(false);
  const moreBulkActionsRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!showMoreBulkActions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (moreBulkActionsRef.current && !moreBulkActionsRef.current.contains(e.target as Node)) {
        setShowMoreBulkActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreBulkActions]);
  const [showProfileDropdown, setShowProfileDropdown] = React.useState(false);
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [showWorkspaceSettings, setShowWorkspaceSettings] = React.useState(false);
  const [showTeamAssignModal, setShowTeamAssignModal] = React.useState(false);
  const [profileData, setProfileData] = React.useState(userProfile);
  const profileAvatarRef = React.useRef<HTMLDivElement>(null);

  // Keep the local profile copy in sync when the prop changes (e.g. App.tsx
  // merges effective workspace permissions after resolving them) so the tab
  // gate and sidebar always reflect the member's actual module access.
  React.useEffect(() => {
    if (!userProfile) return;
    setProfileData(prev => {
      if (!prev) return userProfile;
      const next = { ...prev, ...userProfile };
      if (next.workspaceRole === prev.workspaceRole
        && JSON.stringify(next.permissions) === JSON.stringify(prev.permissions)) return prev;
      return next;
    });
  }, [userProfile]);
  const profileDropdownRef = React.useRef<HTMLDivElement>(null);

  // Workspace Settings in THIS shell's own profile dropdown (this menu is
  // AdminDashboard's own — it is not the shared UserMenu), gated by the
  // single shared predicate in src/lib/permissions.ts that UserMenu also
  // consumes: genuine workspace admins and platform admins only. External
  // org accounts never see it — org approval stamps them workspace
  // 'admin' as a record of org ownership, which is not workspace
  // adminship — and the item stays hidden while memberships resolve.
  const canSeeWorkspaceSettings = canSeeWorkspaceSettingsGate(profileData, {
    platformAdmin,
    activeWorkspaceRole: activeWorkspace?.role,
    workspacesLoading,
  });

  // Single client-side source of truth for every module this member may
  // actually access (mirrors the server's effective-identity grants). Each
  // module's booleans drive what data is FETCHED (permission filtering happens
  // before anything is loaded), what renders, and which edit controls appear.
  const moduleLevel = React.useMemo(() => {
    const level = (m: string) => moduleAccessLevel(m, profileData, isPlatformAdmin);
    const can = (m: string) => level(m) !== 'none';
    return {
      dashboard: true,
      workflow: can('workflow'),
      workflowEdit: level('workflow') === 'edit',
      applicants: can('applicants'),
      applicantsEdit: level('applicants') === 'edit',
      batch: can('batch'),
      batchEdit: level('batch') === 'edit',
      events: can('events'),
      eventsEdit: level('events') === 'edit',
      myEvents: can('my-events'),
      myEventsEdit: level('my-events') === 'edit',
      eventsAny: can('events') || can('my-events'),
      tickets: can('tickets'),
      ticketsEdit: level('tickets') === 'edit',
      users: can('users'),
      finance: can('finance'),
      financeEdit: level('finance') === 'edit',
      reports: can('reports'),
      reportsEdit: level('reports') === 'edit',
      approvals: can('approvals'),
      approvalsEdit: level('approvals') === 'edit',
      jobs: can('jobs'),
      jobsEdit: level('jobs') === 'edit',
      offers: can('offers'),
      offersEdit: level('offers') === 'edit',
      employers: can('employers'),
      employersEdit: level('employers') === 'edit',
      referrers: can('referrers'),
      referrersEdit: level('referrers') === 'edit',
      sponsors: can('sponsors'),
      sponsorsEdit: level('sponsors') === 'edit',
      organizations: can('organizations'),
      organizationsEdit: level('organizations') === 'edit',
      settings: can('settings'),
      templates: can('templates'),
      templatesEdit: level('templates') === 'edit',
      docTemplates: can('doc-templates'),
      docTemplatesEdit: level('doc-templates') === 'edit',
      invitations: can('invitations'),
      invitationsEdit: level('invitations') === 'edit',
      workspaces: can('workspaces'),
      aiSuggestions: can('ai-suggestions'),
      aiSuggestionsEdit: level('ai-suggestions') === 'edit',
    };
  }, [profileData, isPlatformAdmin]);

  // Applicant rows are consumed by many modules (Batch, Events, Tickets,
  // Users, Finance, Reports, Jobs, Employers, Referrers, Sponsors…), so only
  // skip loading them when the member cannot reach ANY of those modules.
  const applicantBackedModules = [
    'applicants', 'batch', 'events', 'myEvents', 'tickets', 'users',
    'finance', 'reports', 'jobs', 'employers', 'referrers', 'sponsors', 'organizations',
  ] as const;
  const needsApplicantData = applicantBackedModules.some((m) => moduleLevel[m]);

  // Self-heal the UID-based admin custom claim once on mount. After the
  // email→UID migration, admins whose claim is missing from their token get
  // "Missing or insufficient permissions" on client-side Firestore writes
  // (Firestore rules authorise admins ONLY via request.auth.token.admin).
  // The server syncs the claim from the profile during this request, then we
  // force-refresh so subsequent writes carry the restored claim.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token || cancelled) return;
        const res = await fetch('/api/admin/verify-admin-claim', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.shouldBeAdmin && !data.admin) {
          await auth.currentUser?.getIdToken(true);
        }
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const { programs } = usePrograms();
  // Pass the module grants into the data hooks so restricted modules are never
  // fetched into client state in the first place (permission filtering before
  // load — the dashboard no longer hydrates modules the member cannot access).
  const appCtx = useApplications(adminEmail, { enabled: needsApplicantData });

  // Sub-view navigation state for Applicants tab (Level 1: Hub, Level 2: Drill-down / Archived)
  const [applicantSubView, setApplicantSubView] = React.useState<'hub' | 'archived'>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('view') === 'archived') return 'archived';
    }
    return 'hub';
  });

  const updateUrlQueryParams = React.useCallback((params: Record<string, string | null>) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([key, val]) => {
      if (val === null || val === undefined) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, val);
      }
    });
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Sync initial query params for program drill-down or view state
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const initialProgram = params.get('program');
    if (initialProgram) {
      appCtx.setProgramFilter([initialProgram]);
    }
  }, []);

  // Modal stacking layers for AdminDashboard's own full-screen overlays.
  const archiveZ = useModalLayer(!!appCtx.showArchiveModal);

  // Lock body scroll when any inline modal is open
  useBodyScrollLock(!!appCtx.showArchiveModal || !!appCtx.isCreatingCandidate);
  const eventsCtx = useEvents({
    adminEmail,
    applications: appCtx.applications,
    setApplications: appCtx.setApplications,
    enabled: moduleLevel.eventsAny,
  });
  const batchesCtx = useBatches({
    adminEmail,
    applications: appCtx.applications,
    setApplications: appCtx.setApplications,
  });
  const usersCtx = useUsers({
    adminEmail,
    auditLogs: appCtx.auditLogs,
    setAuditLogs: appCtx.setAuditLogs,
  });

  // Route admin/batch toasts through the global notification layer.
  React.useEffect(() => {
    const t = appCtx.toast || batchesCtx.toast;
    if (!t) return;
    notifyToast(t.type === 'error' ? 'error' : 'success', t.message);
  }, [appCtx.toast, batchesCtx.toast]);

  // Close profile dropdown when clicking outside both the avatar button and dropdown
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        profileAvatarRef.current &&
        !profileAvatarRef.current.contains(e.target as Node) &&
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(e.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };
    if (showProfileDropdown) {
      // Use click instead of mousedown so the dropdown button's click handler fires first
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showProfileDropdown]);

  // Listen for bottom-nav tab changes from App.tsx
  const [activePreviewDocument, setActivePreviewDocument] = React.useState<AdminDocumentItem | null>(null);

  // Bumped after every DocumentPreviewModal write (decision / override /
  // retry) so a mounted DocumentsCenter silently refetches and the
  // outcome lands in its list. Other surfaces ignore the signal.
  const [documentsRefreshSignal, setDocumentsRefreshSignal] = React.useState(0);

  // Listen for bottom-nav tab changes from App.tsx
  React.useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail.tab;
      // Acceptance is the canonical tab list (lib/adminTabs, ADM-07);
      // this handler used to re-declare it inline as validAdminTabs.
      if (isAdminTabId(tab)) {
        appCtx.setAdminTab(tab);
      }
    };
    window.addEventListener('nav:change', handler);
    return () => window.removeEventListener('nav:change', handler);
  }, [appCtx.setAdminTab]);

  const getDisplayName = () => {
    if (profileData.displayName) return profileData.displayName;
    if (profileData.firstName || profileData.lastName) return `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();
    return profileData.email;
  };

  const getInitials = () => {
    const dn = getDisplayName();
    if (dn) return dn.charAt(0).toUpperCase();
    return profileData.email.charAt(0).toUpperCase();
  };

  // Fetch ONLY the datasets the member is actually allowed to see. Permission
  // filtering happens BEFORE any request is sent, so restricted modules are
  // never loaded into client state only to be hidden in the UI afterwards.
  // The once-guard also lets this fire again when the effective permission
  // matrix arrives (custom roles load their grants asynchronously).
  const fetchedDatasets = React.useRef<Record<string, boolean>>({});
  React.useEffect(() => {
    if (!appCtx.authReady) return;
    const once = (key: string, fn: () => void) => {
      if (fetchedDatasets.current[key]) return;
      fetchedDatasets.current[key] = true;
      fn();
    };
    if (needsApplicantData) once('applications', () => appCtx.fetchApplications());
    if (moduleLevel.users) once('auditLogs', () => appCtx.fetchAuditLogs());
    if (moduleLevel.batch) once('batches', () => { batchesCtx.fetchBatches(); batchesCtx.fetchBatchFolders(); });
    if (moduleLevel.users) once('users', () => usersCtx.fetchUsers());
    // Events are fetched by useEvents' auth bootstrap when the module is granted.
  }, [appCtx.authReady, needsApplicantData, moduleLevel]);

  // Navigate to another section — mirror App.tsx handleNavChange so the
  // dark sidebar's active highlight follows dashboard widget clicks.
  const handleNavChangeLocal = React.useCallback((tabId: string) => {
    window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab: tabId } }));
  }, []);

  // Dashboard tickets (org-wide) for the Support Tickets widget — only fetched
  // when the member holds Tickets module access (No Access → never requested).
  const [ticketsList, setTicketsList] = React.useState<any[]>([]);
  React.useEffect(() => {
    if (!moduleLevel.tickets) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/tickets', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setTicketsList(data.tickets || []);
      } catch { /* dashboard widget is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [moduleLevel.tickets]);

  // Handle wizard completion — bridge wizard data into existing create-candidate API
  const handleWizardComplete = async (wizardData: {
    programId: string; programName: string; term: string;
    requirementsConfirmed: boolean; resumeUploaded: boolean; resumeParsed: boolean;
    parsedFirstName?: string; parsedLastName?: string; parsedEmail?: string; parsedPhone?: string;
  }) => {
    // Pre-fill candidate fields from wizard data.
    // Also pass the wizard values directly to handleCreateNewCandidate to
    // work around React state batching — setCandidate* updates haven't flushed
    // by the time handleCreateNewCandidate reads them.
    if (wizardData.parsedFirstName) appCtx.setCandidateFirstName(wizardData.parsedFirstName);
    if (wizardData.parsedLastName) appCtx.setCandidateLastName(wizardData.parsedLastName);
    if (wizardData.parsedEmail) appCtx.setCandidateEmail(wizardData.parsedEmail);
    appCtx.setCandidateProgram(wizardData.programId);
    appCtx.setCandidateProgramTerm(wizardData.term);
    appCtx.setSendWelcomeEmail(true);
    appCtx.setCandidateRequirementsConfirmed(wizardData.requirementsConfirmed);
    appCtx.setCandidateResumeUploaded(wizardData.resumeUploaded);
    appCtx.setCandidateResumeParsed(wizardData.resumeParsed);

    // Pass wizard data directly to avoid React state batching timing issues
    await appCtx.handleCreateNewCandidate(
      { preventDefault: () => {} } as React.FormEvent,
      wizardData.term,
      wizardData.parsedFirstName,
      wizardData.parsedLastName,
      wizardData.parsedEmail,
      wizardData.programId,
    );
  };

  // Applicant previews are read-only popups; full application details remain on the three-column Applicants page.
  const [isCsvImportOpen, setIsCsvImportOpen] = React.useState(false);
  // ADM-08: the ?tab=senders deep-link hint is one-shot. Once the admin
  // leaves Settings, later Settings visits open on the default sub-tab.
  React.useEffect(() => {
    if (appCtx.adminTab !== 'settings' && appCtx.settingsInitialSubTab !== 'programs') {
      appCtx.setSettingsInitialSubTab('programs');
    }
  }, [appCtx.adminTab, appCtx.settingsInitialSubTab, appCtx.setSettingsInitialSubTab]);

  // ADM-13 (decision D5): return path for cross-surface applicant jumps.
  // The origin is consumed once: Back restores the origin tab (or, for an
  // origin inside the applicants tab itself, its sub-view + deselect),
  // and navigating away from Applicants by any other route clears it so
  // no stale Back control lingers.
  const APPLICANT_ORIGIN_LABELS: Record<string, string> = {
    documents: 'Documents',
    workflow: 'Workflow',
    batch: 'Batch Center',
    events: 'Events Calendar',
    jobs: 'Jobs',
    offers: 'Offers',
    'my-events': 'My Events',
    'ai-suggestions': 'AI Suggestions',
    finance: 'Finance',
    signing: 'E-Signatures',
    tickets: 'Support Tickets',
  };
  const applicantOriginLabel = appCtx.applicantReturnOrigin
    ? (appCtx.applicantReturnOrigin.subView === 'archived'
      ? 'Archived Applicants'
      : (APPLICANT_ORIGIN_LABELS[appCtx.applicantReturnOrigin.tab] || 'Previous Page'))
    : null;
  const handleReturnFromApplicant = React.useCallback(() => {
    const origin = appCtx.applicantReturnOrigin;
    if (!origin) return;
    appCtx.setApplicantReturnOrigin(null);
    if (origin.tab === 'applicants') {
      if (origin.subView) setApplicantSubView(origin.subView);
      appCtx.setSelectedApp(null);
    } else {
      appCtx.setAdminTab(origin.tab);
    }
  }, [appCtx.applicantReturnOrigin, appCtx.setApplicantReturnOrigin, appCtx.setAdminTab, appCtx.setSelectedApp]);
  React.useEffect(() => {
    if (appCtx.adminTab !== 'applicants' && appCtx.applicantReturnOrigin) {
      appCtx.setApplicantReturnOrigin(null);
    }
  }, [appCtx.adminTab, appCtx.applicantReturnOrigin, appCtx.setApplicantReturnOrigin]);

  const handleSendPasswordReset = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      notifyToast('success', 'Password reset email sent to ' + email);
    } catch {
      notifyToast('error', 'Failed to send password reset. Please check the email address and try again.');
    }
  };

  // Bulk Email State
  const [showBulkEmailModal, setShowBulkEmailModal] = React.useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = React.useState('');
  const [bulkEmailBody, setBulkEmailBody] = React.useState('');
  const [bulkEmailSending, setBulkEmailSending] = React.useState(false);

  const handleSendBulkEmailModal = async () => {
    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
      notifyToast('warning', 'Please fill in both subject and body.');
      return;
    }
    const selectedApps = appCtx.applications.filter((a: any) =>
      (appCtx.selectedAppIds || []).includes(a.applicationId)
    );
    const validRecipients = selectedApps.filter((a: any) => a.userEmail);
    if (validRecipients.length === 0) {
      notifyToast('warning', 'None of the selected applicants have valid email addresses.');
      return;
    }

    if (!(await confirmDialog(`Queue bulk email job for ${validRecipients.length} selected candidate(s)?`))) return;

    setBulkEmailSending(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const items = validRecipients.map((a: any) => ({
        itemType: 'applicant',
        targetId: a.applicationId,
        recipientEmail: a.userEmail,
        recipientName: [a.personalDetails?.firstName, a.personalDetails?.lastName].filter(Boolean).join(' ') || a.userEmail,
        inputSnapshot: {
          applicationId: a.applicationId,
          email: a.userEmail,
          firstName: a.personalDetails?.firstName || '',
          lastName: a.personalDetails?.lastName || '',
        },
      }));

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'bulk_email',
          items,
          options: {
            subject: bulkEmailSubject.trim(),
            htmlBody: `<p>${bulkEmailBody.trim().replace(/\n/g, '<br/>')}</p>`,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to queue bulk email job');

      notifyToast('success', `Bulk email job queued for ${validRecipients.length} recipients!`);
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
      setShowBulkEmailModal(false);
      setBulkEmailSubject('');
      setBulkEmailBody('');
      appCtx.setSelectedAppIds([]);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to queue bulk email');
    } finally {
      setBulkEmailSending(false);
    }
  };

  // All unique applicant tags for the tag filter dropdown
  // MUST be before the authReady early return — hooks must be called every render
  const allApplicantTags = React.useMemo(() => {
    const tags = new Set<string>();
    appCtx.applications?.forEach((a: any) => a.tags?.forEach((t: string) => tags.add(t)));
    return Array.from(tags).sort();
  }, [appCtx.applications]);

  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = (colId: string) => {
    if (sortColumn === colId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colId);
      setSortDirection('asc');
    }
  };

  const sortedDisplayedApps = React.useMemo(() => {
    const apps = [...(appCtx.displayedApps || [])];
    if (!sortColumn) return apps;
    return apps.sort((a: any, b: any) => {
      let valA: any = '';
      let valB: any = '';
      if (sortColumn === 'firstName') { valA = a.personalDetails?.firstName || ''; valB = b.personalDetails?.firstName || ''; }
      else if (sortColumn === 'lastName') { valA = a.personalDetails?.lastName || ''; valB = b.personalDetails?.lastName || ''; }
      else if (sortColumn === 'email') { valA = a.userEmail || ''; valB = b.userEmail || ''; }
      else if (sortColumn === 'dateApplied') { valA = a.createdAt || ''; valB = b.createdAt || ''; }
      else if (sortColumn === 'status') { valA = getSubStatus(a); valB = getSubStatus(b); }
      else { valA = a[sortColumn] || ''; valB = b[sortColumn] || ''; }

      const cmp = String(valA).localeCompare(String(valB));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [appCtx.displayedApps, sortColumn, sortDirection]);

  const handleExportCsv = () => {
    const apps = appCtx.filteredApps || appCtx.applications || [];
    if (apps.length === 0) {
      notifyToast('warning', 'No applicant records to export.');
      return;
    }
    const headers = ['Application ID', 'First Name', 'Last Name', 'Email', 'Program', 'Status', 'Date Applied'];
    const rows = apps.map((a: any) => [
      a.applicationId || a.id || '',
      a.personalDetails?.firstName || '',
      a.personalDetails?.lastName || '',
      a.userEmail || '',
      a.programType || a.programId || '',
      getSubStatus(a),
      a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''
    ]);
    const csvContent = [headers.map(csvCell).join(','), ...rows.map(r => r.map(v => csvCell(v)).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Applicants_Export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!appCtx.authReady) {
    return (
      <div className="min-h-screen premium-shell flex items-center justify-center animate-fade-in">
        <div className="text-center space-y-4">
          <div className="premium-loader mx-auto" />
          <p className="text-sm text-slate-700 font-bold">Authenticating session...</p>
        </div>
      </div>
    );
  }

  // Derived events list for main Events tab
  const filteredEventsList = eventsCtx.getFilteredEvents ? eventsCtx.getFilteredEvents() : eventsCtx.events;

  // Column definitions for the table view
  const ALL_TABLE_COLUMNS: { id: string; label: string; render: (app: any) => React.ReactNode }[] = [
    { id: 'firstName', label: 'First Name', render: (app: any) => <span className="font-semibold text-slate-800">{app.personalDetails?.firstName || '—'}</span> },
    { id: 'lastName', label: 'Last Name', render: (app: any) => <span className="font-semibold text-slate-800">{app.personalDetails?.lastName || '—'}</span> },
    { id: 'email', label: 'Email', render: (app: any) => <span className="text-slate-600">{app.userEmail || '—'}</span> },
    { id: 'program', label: 'Program', render: (app: any) => {
      const p = (appCtx.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId);
      return <span>{p?.name || app.programType || app.programId || '—'}</span>;
    }},
    { id: 'masterPhase', label: 'Master Phase', render: (app: any) => {
      const matchedProg = (appCtx?.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
      const stages = getPipelineStages(matchedProg);
      const label = stageLabel(stages, app.masterStage, '—');
      return <span className="table-cell-pill pill-neutral">{label}</span>;
    }},
    { id: 'status', label: 'Status', render: (app: any) => {
      const sub = getSubStatus(app);
      const s = (sub || '').toLowerCase();
      const pillClass = s.includes('approved') || s.includes('placed') || s.includes('confirmed') ? 'pill-success' :
                        s.includes('rejected') || s.includes('cancelled') || s.includes('closed') ? 'pill-danger' :
                        s.includes('pending') || s.includes('review') || s.includes('interview') ? 'pill-warning' :
                        'pill-neutral';
      return <span className={`table-cell-pill ${pillClass}`}>{sub}</span>;
    }},
    { id: 'tags', label: 'Tags', render: (app: any) => (
      <div className="flex flex-wrap gap-1">
        {((app as any).tags || []).slice(0, 3).map((t: string) => <span key={t} className="px-1.5 py-0.5 bg-brand-active text-caption font-bold rounded">{t}</span>)}
        {((app as any).tags || []).length > 3 && <span className="text-caption text-slate-400 font-bold">+{((app as any).tags || []).length - 3}</span>}
      </div>
    )},
    { id: 'partner', label: 'Partner', render: (app: any) => {
      const provider = (appCtx.providers || []).find((pr: any) => pr.providerId === (app as any).assignedPartner);
      return <span>{provider?.name || (app as any).assignedPartner || '—'}</span>;
    }},
    { id: 'dateApplied', label: 'Date Applied', render: (app: any) => <span className="text-slate-600 text-caption">{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '—'}</span> },
    { id: 'financeStatus', label: 'Finance Status', render: (app: any) => {
      const fs = (app as any).financialStatus;
      if (!fs) return <span className="text-slate-400">—</span>;
      const colors: Record<string, string> = { paid: 'pill-success', pending: 'pill-warning', overdue: 'pill-danger' };
      return <span className={`table-cell-pill ${colors[fs.toLowerCase()] || 'pill-neutral'}`}>{fs}</span>;
    }},
    { id: 'visaStatus', label: 'Visa Status', render: (app: any) => {
      const vs = app.visaDetails?.visaApprovalStatus;
      if (!vs) return <span className="text-slate-400">—</span>;
      const colors: Record<string, string> = { approved: 'pill-success', pending: 'pill-warning', rejected: 'pill-danger' };
      return <span className={`table-cell-pill ${colors[vs.toLowerCase()] || 'pill-neutral'}`}>{vs}</span>;
    }},
  ];

  // Page-scoped selection for the applicants views (owner rule): the
  // header/toolbar checkboxes act ONLY on the currently displayed page
  // (displayedApps is the page slice) — toggling adds the page's ids or
  // removes exactly those ids, so selections accumulate across pages
  // and are never silently replaced, cleared, or double-counted.
  const displayedAppIds = (appCtx.displayedApps || []).map((a: any) => a.applicationId).filter(Boolean);
  const displayedSelectionShare = pageSelectionShare(displayedAppIds, appCtx.selectedAppIds || []);
  const toggleDisplayedPageSelection = () =>
    appCtx.setSelectedAppIds(togglePageSelection(displayedAppIds, appCtx.selectedAppIds || []));

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden premium-shell text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900">
      {/* Toasts render via the global root-level notification layer. */}

      {/* CRM Top Bar — refined with subtle glass treatment */}
      <div className="h-16 bg-white/90 border-b border-slate-200/70 flex items-center px-6 justify-between shrink-0 shadow-sm backdrop-blur-xl">
        <div className="ml-auto flex items-center gap-2">
        {/* Backend Tasks Clock Icon */}
        <TaskNotificationCenter />
        {/* Real-time notification center */}
        <NotificationCenter />
        {/* Profile Avatar Button */}
        <div ref={profileAvatarRef} className="relative">
          <button
            aria-label="Profile menu"
            title="Profile menu"
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
          >
            <div className="h-8 w-8 rounded-full btn-primary flex items-center justify-center text-sm font-extrabold text-white shadow-sm overflow-hidden">
              {profileData.profilePhotoUrl ? (
                <img src={profileData.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                getInitials()
              )}
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>
        </div>
        </div>
      </div>

      <NotificationBanner />

      {/* Profile Dropdown (portal to body to escape overflow-hidden) */}
      {showProfileDropdown && ReactDOM.createPortal(
        <div ref={profileDropdownRef} className="fixed right-4 top-[calc(4rem+0.375rem)] w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 py-2 z-[9999] animate-fade-in"
          onClick={e => e.stopPropagation()}>
          {/* Profile header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full btn-primary flex items-center justify-center text-base font-extrabold text-white shadow-sm overflow-hidden shrink-0">
              {profileData.profilePhotoUrl ? (
                <img src={profileData.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                getInitials()
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-extrabold text-slate-900 truncate">{getDisplayName()}</p>
              <p className="text-caption text-slate-400 truncate">{profileData.email}</p>
            </div>
          </div>

          {/* Notification Toggle */}
          <div className="px-4 py-2 border-b border-slate-100">
            <NotificationToggleInline />
          </div>

          {/* My Profile */}
          <button
            onClick={() => { setShowProfileDropdown(false); setShowProfileModal(true); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
          >
            <UserCircle className="h-4 w-4 text-slate-400" />
            My Profile
          </button>

          {/* Workspace Settings — internal staff only. Never rendered
              for external org accounts (see canSeeWorkspaceSettings in
              src/lib/permissions.ts): org approval stamps them workspace
              'admin' as a record of org ownership, which is not workspace
              adminship. */}
          {canSeeWorkspaceSettings && (
            <button
              onClick={() => { setShowProfileDropdown(false); setShowWorkspaceSettings(true); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
            >
              <Building2 className="h-4 w-4 text-slate-400" />
              Workspace Settings
            </button>
          )}

          {/* Logout */}
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>,
        document.body
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          profile={profileData}
          onClose={() => setShowProfileModal(false)}
          onProfileUpdated={(updated) => {
            setProfileData(prev => ({ ...prev, ...updated }));
            setShowProfileModal(false);
          }}
          isAdminEditing={false}
        />
      )}

      {/* The settings modal is not even mounted for accounts that may
          never open it — no state path can surface the main workspace's
          settings to an external org account (mirrors UserMenu). */}
      {canSeeWorkspaceSettings && (
        <WorkspaceSettings open={showWorkspaceSettings} onClose={() => setShowWorkspaceSettings(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {!hasModuleAccess(appCtx.adminTab, profileData, isPlatformAdmin) ? (
          <div className="h-full overflow-y-auto p-8 flex items-center justify-center">
            <div className="bg-white rounded-3xl border border-slate-200 p-8 max-w-md text-center shadow-lg">
              <div className="h-14 w-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <h3 className="text-lg font-black text-slate-900">Access Restricted</h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Your workspace role does not have permission to view or manage the <strong className="capitalize text-slate-700">{appCtx.adminTab}</strong> module.
              </p>
              <button
                onClick={() => {
                  // Never bounce a restricted member into another module they
                  // cannot access — send them to the dashboard (always allowed).
                  appCtx.setAdminTab('dashboard');
                }}
                className="mt-6 px-5 py-2.5 rounded-xl btn-primary text-white text-xs font-bold shadow-md cursor-pointer inline-flex items-center gap-2"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        ) : appCtx.adminTab === 'dashboard' ? (
          <div className="h-full overflow-y-auto p-6 animate-fade-in">
            <DashboardShell
              role="admin"
              userProfile={profileData}
              onNavigate={(tab) => {
                // The shell emits plain strings; only canonical admin
                // tab ids may land in adminTab (ADM-07 — no `as any`).
                if (isAdminTabId(tab)) appCtx.setAdminTab(tab);
                handleNavChangeLocal(tab);
              }}
              applications={appCtx.applications}
              events={eventsCtx.events}
              tickets={ticketsList}
              loading={appCtx.loading}
            />
          </div>
        ) : appCtx.adminTab === 'documents' ? (
          <div className="h-full overflow-y-auto p-6 animate-fade-in">
            <DocumentsCenter
              refreshSignal={documentsRefreshSignal}
              onSelectApplication={(appId) => {
                const targetApp = appCtx.applications.find(a => a.id === appId || a.applicationId === appId);
                if (targetApp) appCtx.viewApplicantInWorkspace(targetApp, { tab: 'documents' });
              }}
              onPreviewDocument={(doc) => setActivePreviewDocument(doc)}
            />
          </div>
        ) : appCtx.adminTab === 'automations' ? (
          <div className="h-full overflow-y-auto"><AutomationManager /></div>
        ) : appCtx.adminTab === 'workflow' ? (
          <div className="h-full overflow-y-auto"><WorkflowPage onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'workflow' })} /></div>
        ) : appCtx.adminTab === 'bulk-jobs' ? (
          <div className="h-full overflow-y-auto p-6 animate-fade-in">
            <BulkJobsDashboard initialJobId={new URLSearchParams(window.location.search).get('jobId')} />
          </div>
        ) : appCtx.adminTab === 'batch' ? (
          <div className="h-full overflow-y-auto p-6 animate-fade-in"><BatchCenter
            batches={batchesCtx.batches}
            batchFolders={batchesCtx.batchFolders}
            currentFolderId={batchesCtx.currentFolderId}
            setCurrentFolderId={batchesCtx.setCurrentFolderId}
            onFetchBatchFolders={batchesCtx.fetchBatchFolders}
            onCreateFolder={batchesCtx.handleCreateFolder}
            onUpdateFolder={batchesCtx.handleUpdateFolder}
            onDeleteFolder={batchesCtx.handleDeleteFolder}
            applications={appCtx.applications}
            canEditBatch={moduleLevel.batchEdit}
            isCreatingBatch={batchesCtx.isCreatingBatch}
            selectedBatchId={batchesCtx.selectedBatchId}
            batchName={batchesCtx.batchName}
            batchDescription={batchesCtx.batchDescription}
            batchProgramTypes={batchesCtx.batchProgramTypes}
            batchSearchQuery={batchesCtx.batchSearchQuery}
            batchSelectedApplicantIds={batchesCtx.batchSelectedApplicantIds}
            bulkEmailSubject={batchesCtx.bulkEmailSubject}
            bulkEmailContent={batchesCtx.bulkEmailContent}
            broadcastSubject={batchesCtx.broadcastSubject}
            broadcastContent={batchesCtx.broadcastContent}
            broadcastAttachments={batchesCtx.broadcastAttachments}
            broadcasting={batchesCtx.broadcasting}
            broadcastProgress={batchesCtx.broadcastProgress}
            broadcastSummary={batchesCtx.broadcastSummary}
            bulkStatusValue={batchesCtx.bulkStatusValue}
            cohortSearch={batchesCtx.cohortSearch}
            cohortStatusFilter={batchesCtx.cohortStatusFilter}
            cohortProgramFilter={batchesCtx.cohortProgramFilter}
            batchLogs={batchesCtx.batchLogs}
            isLoadingLogs={batchesCtx.isLoadingLogs}
            newBatchLogText={batchesCtx.newBatchLogText}
            isAddApplicantOpen={batchesCtx.isAddApplicantOpen}
            addApplicantSearch={batchesCtx.addApplicantSearch}
            batchNotes={batchesCtx.batchNotes}
            isSavingNotes={batchesCtx.isSavingNotes}
            adminEmail={adminEmail}
            batchLogFile={batchesCtx.batchLogFile ?? null}
            batchLogFileProgress={batchesCtx.batchLogFileProgress ?? null}
            isUploadingBatchLogFile={batchesCtx.isUploadingBatchLogFile ?? false}
            onBatchLogFileChange={batchesCtx.handleBatchLogFileChange}
            selectedApp={appCtx.selectedApp}
            previewDoc={appCtx.previewDoc}
            setPreviewDoc={appCtx.setPreviewDoc}
            setBatchLogFile={batchesCtx.setBatchLogFile}
            setBatchLogFileProgress={batchesCtx.setBatchLogFileProgress}
            setIsUploadingBatchLogFile={batchesCtx.setIsUploadingBatchLogFile}
            setIsCreatingBatch={batchesCtx.setIsCreatingBatch}
            setSelectedBatchId={batchesCtx.setSelectedBatchId}
            setBatchName={batchesCtx.setBatchName}
            setBatchDescription={batchesCtx.setBatchDescription}
            setBatchProgramTypes={batchesCtx.setBatchProgramTypes}
            setBatchSearchQuery={batchesCtx.setBatchSearchQuery}
            setBatchSelectedApplicantIds={batchesCtx.setBatchSelectedApplicantIds}
            setBulkEmailSubject={batchesCtx.setBulkEmailSubject}
            setBulkEmailContent={batchesCtx.setBulkEmailContent}
            setBroadcastSubject={batchesCtx.setBroadcastSubject}
            setBroadcastContent={batchesCtx.setBroadcastContent}
            setBroadcastAttachments={batchesCtx.setBroadcastAttachments}
            setBroadcastSummary={batchesCtx.setBroadcastSummary}
            setBulkStatusValue={batchesCtx.setBulkStatusValue}
            setCohortSearch={batchesCtx.setCohortSearch}
            setCohortStatusFilter={batchesCtx.setCohortStatusFilter}
            setCohortProgramFilter={batchesCtx.setCohortProgramFilter}
            setBatchLogs={batchesCtx.setBatchLogs}
            setIsLoadingLogs={batchesCtx.setIsLoadingLogs}
            setNewBatchLogText={batchesCtx.setNewBatchLogText}
            setIsAddApplicantOpen={batchesCtx.setIsAddApplicantOpen}
            setAddApplicantSearch={batchesCtx.setAddApplicantSearch}
            setBatchNotes={batchesCtx.setBatchNotes}
            setIsSavingNotes={batchesCtx.setIsSavingNotes}
            onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'batch' })}
            onFetchBatches={batchesCtx.fetchBatches}
            onCreateBatch={batchesCtx.handleCreateBatch}
            onDeleteBatch={batchesCtx.handleDeleteBatch}
            onUpdateBatchMembers={batchesCtx.handleUpdateBatchMembers}
            onSaveBatchNotes={batchesCtx.handleSaveBatchNotes}
            onUpdateBatchDetail={batchesCtx.handleUpdateBatchDetail}
            onUpdateBatchTags={batchesCtx.handleUpdateBatchTags}
            programsList={appCtx.programsList}
            appCtxProviders={appCtx.providers}
            onBulkStatusUpdate={batchesCtx.handleBulkStatusUpdate}
            onBulkPartnerUpdate={appCtx.handleBulkPartnerChange}
            onSendBulkEmail={batchesCtx.handleSendBulkEmail}
            onBroadcastEmail={batchesCtx.handleBroadcastEmail}
            onFetchBatchLogs={batchesCtx.fetchBatchLogs}
            onAddBatchLog={batchesCtx.handleAddBatchLog}
            onCreateAuditLog={batchesCtx.createAuditLog}
            onNavigateToApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'batch' })}
          /></div>
        ) : appCtx.adminTab === 'events' ? (
          <div className="h-full overflow-y-auto p-6"><EventsManager
            events={eventsCtx.events}
            applications={appCtx.applications}
            isCreatingEvent={eventsCtx.isCreatingEvent}
            isEditingEventId={eventsCtx.isEditingEventId}
            eventViewMode={eventsCtx.eventViewMode}
            calendarViewType={eventsCtx.calendarViewType}
            currentCalendarDate={eventsCtx.currentCalendarDate}
            eventSearchQuery={eventsCtx.eventSearchQuery}
            filterEventType={eventsCtx.filterEventType}
            filterEventStatus={eventsCtx.filterEventStatus}
            filterEventApplicant={eventsCtx.filterEventApplicant}
            filterDateStart={eventsCtx.filterDateStart}
            filterDateEnd={eventsCtx.filterDateEnd}
            filterDatePreset={eventsCtx.filterDatePreset}
            focusedEvent={eventsCtx.focusedEvent}
            filteredEvents={filteredEventsList}
            eventsPage={eventsCtx.eventsPage}
            eventsPageSize={20}
            eventsTotalPages={eventsCtx.eventsTotalPages}
            displayedEvents={eventsCtx.displayedEvents}
            eventTitle={eventsCtx.eventTitle}
            eventType={eventsCtx.eventType}
            eventDateTime={eventsCtx.eventDateTime}
            eventTimezone={eventsCtx.eventTimezone}
            eventLocation={eventsCtx.eventLocation}
            eventDescription={eventsCtx.eventDescription}
            eventInternalNotes={eventsCtx.eventInternalNotes}
            eventExternalNotes={eventsCtx.eventExternalNotes}
            eventStatus={eventsCtx.eventStatus}
            eventApplicantId={eventsCtx.eventApplicantId}
            eventNotifyApplicants={eventsCtx.eventNotifyApplicants}
            isVirtualMeeting={eventsCtx.isVirtualMeeting}
            eventAssignedTo={eventsCtx.eventAssignedTo}
            eventAssignedToName={eventsCtx.eventAssignedToName}
            agentsList={eventsCtx.agentsList}
            agentsLoaded={eventsCtx.agentsLoaded}
            agentsError={eventsCtx.agentsError}
            adminEmail={adminEmail}
            setEventsPage={eventsCtx.setEventsPage}
            setEventViewMode={eventsCtx.setEventViewMode}
            setCurrentCalendarDate={eventsCtx.setCurrentCalendarDate}
            setEventSearchQuery={eventsCtx.setEventSearchQuery}
            setFilterEventType={eventsCtx.setFilterEventType}
            setFilterEventStatus={eventsCtx.setFilterEventStatus}
            setFilterEventApplicant={eventsCtx.setFilterEventApplicant}
            setFilterDateStart={eventsCtx.setFilterDateStart}
            setFilterDateEnd={eventsCtx.setFilterDateEnd}
            setFilterDatePreset={eventsCtx.setFilterDatePreset}
            setFocusedEvent={eventsCtx.setFocusedEvent}
            setEventTitle={eventsCtx.setEventTitle}
            setEventType={eventsCtx.setEventType}
            setEventDateTime={eventsCtx.setEventDateTime}
            setEventTimezone={eventsCtx.setEventTimezone}
            setEventLocation={eventsCtx.setEventLocation}
            setEventDescription={eventsCtx.setEventDescription}
            setEventInternalNotes={eventsCtx.setEventInternalNotes}
            setEventExternalNotes={eventsCtx.setEventExternalNotes}
            setEventStatus={eventsCtx.setEventStatus}
            setEventApplicantId={eventsCtx.setEventApplicantId}
            setEventNotifyApplicants={eventsCtx.setEventNotifyApplicants}
            setIsVirtualMeeting={eventsCtx.setIsVirtualMeeting}
            setEventAssignedTo={eventsCtx.setEventAssignedTo}
            setEventAssignedToName={eventsCtx.setEventAssignedToName}
            setIsCreatingEvent={eventsCtx.setIsCreatingEvent}
            setIsEditingEventId={eventsCtx.setIsEditingEventId}
            onFetchEvents={eventsCtx.fetchEvents}
            onFetchAgents={eventsCtx.fetchAgents}
            onSaveEvent={eventsCtx.handleSaveEvent}
            onDeleteEvent={eventsCtx.handleDeleteEvent}
            onEditEventClick={eventsCtx.handleEditEventClick}
            onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'events' })}
            canEdit={moduleLevel.eventsEdit}
          /></div>
        ) : appCtx.adminTab === 'reports' ? (
          <div className="h-full overflow-y-auto"><AdminReports allUsers={usersCtx.allUsers} applications={appCtx.applications} programsList={appCtx.programsList} canEdit={moduleLevel.reportsEdit} /></div>
        ) : appCtx.adminTab === 'approvals' ? (
          <div className="h-full overflow-y-auto"><PendingApprovalsManager canEdit={moduleLevel.approvalsEdit} /></div>
        ) : appCtx.adminTab === 'settings' ? (
          <div className="h-full overflow-y-auto"><AdminSettings profile={profileData} initialSubTab={appCtx.settingsInitialSubTab} /></div>
        ) : appCtx.adminTab === 'templates' ? (
          <div className="h-full overflow-y-auto p-6"><EmailTemplateManager canEdit={moduleLevel.templatesEdit} /></div>
        ) : appCtx.adminTab === 'doc-templates' ? (
          <div className="h-full overflow-y-auto p-6"><DocumentTemplateManager adminEmail={adminEmail} canEdit={moduleLevel.docTemplatesEdit} /></div>
        ) : appCtx.adminTab === 'users' ? (
          <div className="h-full overflow-y-auto p-6"><UsersTab
            allUsers={usersCtx.allUsers}
            usersLoading={usersCtx.usersLoading}
            selectedUser={usersCtx.selectedUser}
            isBlockingUser={usersCtx.isBlockingUser}
            applications={appCtx.applications}
            onRefresh={usersCtx.fetchUsers}
            onSelectUser={usersCtx.setSelectedUser}
            onToggleBlock={usersCtx.handleToggleBlockUser}
            onSendPasswordReset={handleSendPasswordReset}
            onCreateUser={usersCtx.createUser}
            onSendPromotionCode={usersCtx.sendPromotionCode}
            onVerifyPromotionCode={usersCtx.verifyPromotionCode}
            onSendRoleChangeCode={usersCtx.sendRoleChangeCode}
            onVerifyRoleChangeCode={usersCtx.verifyRoleChangeCode}
            onChangeRoleDirect={usersCtx.changeRoleDirect}
            onPromoteToEmployer={usersCtx.promoteToEmployer}
            onFetchEmployersList={usersCtx.fetchEmployersList}
            canEdit={moduleLevel.usersEdit}
          /></div>
        ) : appCtx.adminTab === 'tickets' ? (
          <div className="h-full overflow-y-auto p-3.5 sm:p-4 animate-fade-in"><TicketsPage
            applications={appCtx.applications}
            programsList={appCtx.programsList}
            allAdmins={usersCtx.allUsers?.filter((u: any) => {
              const r = (u.workspaceRole || u.role || '').toLowerCase();
              return r && !['applicant', 'job_seeker', 'candidate'].includes(r);
            }).map((u: any) => {
              const displayName = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown';
              return { uid: u.uid, name: displayName, email: u.email || '' };
            })}
            appCtx={appCtx}
            canEdit={moduleLevel.ticketsEdit}
          /></div>
        ) : appCtx.adminTab === 'jobs' ? (
          <div className="h-full flex flex-col">
            <AdminJobsTabBar activeTab="jobs" onSelect={handleNavChangeLocal} />
            <div className="flex-1 min-h-0 overflow-y-auto"><JobsManager onViewApplicantProfile={(appData) => { appCtx.viewApplicantInWorkspace(appData, { tab: 'jobs' }); }} canEdit={moduleLevel.jobsEdit} /></div>
          </div>
        ) : appCtx.adminTab === 'offers' ? (
          <div className="h-full flex flex-col">
            <AdminJobsTabBar activeTab="offers" onSelect={handleNavChangeLocal} />
            <div className="flex-1 min-h-0 overflow-y-auto p-6"><AdminOffersManager canEdit={moduleLevel.offersEdit} onViewApplicant={(applicationId) => { const app = appCtx.applications.find((a: any) => a.id === applicationId || a.applicationId === applicationId); if (app) appCtx.viewApplicantInWorkspace(app, { tab: 'offers' }); }} /></div>
          </div>
        ) : appCtx.adminTab === 'employers' ? (
          <div className="h-full overflow-y-auto"><EmployersManager canEdit={moduleLevel.employersEdit} /></div>
        ) : appCtx.adminTab === 'referrers' ? (
          <div className="h-full overflow-y-auto"><ReferrersManager canEdit={moduleLevel.referrersEdit} /></div>
        ) : appCtx.adminTab === 'sponsors' ? (
          <div className="h-full overflow-y-auto"><SponsorsManager canEdit={moduleLevel.sponsorsEdit} /></div>
        ) : appCtx.adminTab === 'invitations' ? (
          <div className="h-full flex flex-col">
            <AdminJobsTabBar activeTab="invitations" onSelect={handleNavChangeLocal} />
            <div className="flex-1 min-h-0 overflow-y-auto p-6"><AdminInvitationsManager canEdit={moduleLevel.invitationsEdit} /></div>
          </div>
        ) : appCtx.adminTab === 'my-events' ? (
          <div className="h-full overflow-y-auto">
            <MyEventsView onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'my-events' })} canEdit={moduleLevel.myEventsEdit} />
          </div>
        ) : appCtx.adminTab === 'workspaces' && isPlatformAdmin ? (
          <div className="h-full overflow-y-auto p-6">
            <WorkspaceManagement />
          </div>
        ) : appCtx.adminTab === 'ai-suggestions' ? (
          <div className="h-full overflow-y-auto">
            <AiSuggestionsManager canEdit={moduleLevel.aiSuggestionsEdit} onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'ai-suggestions' })} />
          </div>
        ) : appCtx.adminTab === 'organizations' ? (
          <div className="h-full overflow-y-auto">
            <OrgAdminArea canEdit={moduleLevel.organizationsEdit} />
          </div>
        ) : appCtx.adminTab === 'finance' ? (
          <div className="h-full overflow-y-auto p-6">
            <FinanceTab
              applications={appCtx.applications}
              branding={activeWorkspace?.branding}
              onViewApplicant={(app) => appCtx.viewApplicantInWorkspace(app, { tab: 'finance' })}
              canEdit={moduleLevel.financeEdit}
            />
          </div>
        ) : appCtx.adminTab === 'signing' ? (
          <div className="h-full overflow-y-auto p-6 animate-fade-in">
            <EsignaturesPage
              onViewApplication={(applicationId) => {
                const target = appCtx.applications.find(
                  (a: any) => a.id === applicationId || a.applicationId === applicationId
                );
                if (target) appCtx.viewApplicantInWorkspace(target, { tab: 'signing' });
              }}
            />
          </div>
        ) : appCtx.selectedApp ? (
          <div className="flex flex-col md:flex-row h-full min-h-0 overflow-hidden">
            <div className={`hidden md:flex flex-col bg-white border-r border-slate-200 transition-all duration-200 ${applicantListCollapsed ? 'w-16 min-w-[64px]' : 'w-72 min-w-[288px]'}`}>
              <div className={`px-4 py-3 border-b border-slate-200 shrink-0 space-y-3 ${applicantListCollapsed ? 'px-2' : ''}`}>
                <div className={`flex items-center ${applicantListCollapsed ? 'justify-center flex-col gap-2' : 'justify-between'}`}>
                  {!applicantListCollapsed && <h2 className="text-sm font-bold text-slate-800">Applicants</h2>}
                  <div className="flex items-center gap-1">
                    {!applicantListCollapsed && (
                      <>
                        {/* Archive/Delete are destructive mutations — View Only must not expose them. */}
                        {moduleLevel.applicantsEdit && (appCtx.selectedAppIds || []).length > 0 && (
                          <>
                            <button
                              onClick={() => appCtx.setShowArchiveModal(true)}
                              className="p-2 rounded-lg hover:bg-rose-100 text-rose-600 transition-all cursor-pointer"
                              title={`Archive ${(appCtx.selectedAppIds || []).length} selected`}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                            <button
                              onClick={async () => {
                                const ids = appCtx.selectedAppIds || [];
                                if (!(await confirmDialog(`Permanently delete ${ids.length} applicant(s)? This cannot be undone.`))) return;
                                try {
                                  const token = await auth.currentUser?.getIdToken();
                                  const res = await fetch('/api/admin/applications/delete', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                    body: JSON.stringify({ applicationIds: ids }),
                                  });
                                  const data = await res.json();
                                  if (!res.ok) throw new Error(data.error || 'Delete failed');
                                  appCtx.setSelectedAppIds([]);
                                  localStorage.removeItem('all_cached_applications');
                                  localStorage.removeItem('all_cached_applications_at');
                                  await appCtx.fetchApplications();
                                  notifyToast('success', `Successfully deleted ${data.deletedCount} applicant(s).`);
                                } catch (err: any) {
                                  notifyToast('error', 'Delete failed: ' + (err.message || 'Unknown error'));
                                }
                              }}
                              className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-all cursor-pointer"
                              title={`Delete ${(appCtx.selectedAppIds || []).length} selected`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button aria-label="Refresh applicants" title="Refresh applicants" onClick={() => appCtx.fetchApplications()} className="p-2 rounded-lg hover:bg-slate-100 transition-all cursor-pointer"><RefreshCw className="h-4 w-4 text-slate-400" /></button>
                      </>
                    )}
                    <button type="button" onClick={() => setApplicantListCollapsed(v => !v)} className="p-2 rounded-lg hover:bg-slate-100 transition-all cursor-pointer" title={applicantListCollapsed ? 'Expand applicant list' : 'Collapse applicant list'}>
                      {applicantListCollapsed ? <PanelLeftOpen className="h-4 w-4 text-slate-500" /> : <PanelLeftClose className="h-4 w-4 text-slate-500" />}
                    </button>
                  </div>
                </div>
                {!applicantListCollapsed && (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <input
                        value={appCtx.searchTerm || ''}
                        onChange={(e) => { appCtx.setSearchTerm(e.target.value); appCtx.setCurrentPage(1); }}
                        placeholder="Search applicants..."
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs font-semibold text-slate-700 outline-none  focus:bg-white"
                      />
                    </div>
                    {moduleLevel.applicantsEdit && (
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={displayedSelectionShare === 'all'}
                            ref={(el) => {
                              if (el) el.indeterminate = displayedSelectionShare === 'some';
                            }}
                            onChange={toggleDisplayedPageSelection}
                            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                          />
                          <span className="text-[11px] font-semibold text-slate-500">
                            {(appCtx.selectedAppIds || []).length > 0
                              ? `${(appCtx.selectedAppIds || []).length} selected`
                              : 'Select page'}
                          </span>
                        </label>
                        {(appCtx.selectedAppIds || []).length > 0 && (
                          <button
                            onClick={() => appCtx.setSelectedAppIds([])}
                            className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {appCtx.displayedApps?.map((app: any) => (
                  <div key={app.applicationId} className={`flex items-center gap-1 transition-all ${appCtx.selectedApp?.applicationId === app.applicationId ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    {!applicantListCollapsed && moduleLevel.applicantsEdit && (
                      <div className="pl-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={appCtx.selectedAppIds.includes(app.applicationId)}
                          onChange={(e) => {
                            const prev = appCtx.selectedAppIds || [];
                            const next = e.target.checked
                              ? [...prev, app.applicationId]
                              : prev.filter((id: string) => id !== app.applicationId);
                            appCtx.setSelectedAppIds(next);
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                        />
                      </div>
                    )}
                    <button onClick={() => appCtx.handleSelectApplicant(app)} className={`w-full text-left p-3 transition-all flex items-center gap-3 border-l-4 ${appCtx.selectedApp?.applicationId === app.applicationId ? 'bg-blue-50 border-brand shadow-inner' : 'border-transparent hover:bg-slate-50'}`}>
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0 overflow-hidden">
                        {app.profilePhotoUrl ? <img src={app.profilePhotoUrl} className="h-full w-full object-cover" alt="" /> : app.personalDetails?.firstName?.charAt(0) || app.userEmail?.charAt(0).toUpperCase() || '?'}
                      </div>
                      {!applicantListCollapsed && <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-900 truncate">{app.personalDetails?.firstName || '—'} {app.personalDetails?.lastName || ''}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-caption font-semibold bg-emerald-50 text-emerald-700 font-mono">{app.applicationId}</span>
                          {app.applicantVerifiedAt && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-caption font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <svg className="h-2.5 w-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 8l3 3 7-7"/></svg>
                              Verified User
                            </span>
                          )}
                          <span className="inline-flex px-2 py-0.5 rounded-full text-caption font-semibold bg-brand-active">{(() => { const p = (appCtx.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId); return p?.name || app.programType || app.programId || 'Program'; })()}</span>
                        </div>
                        <p className="mt-1 text-caption font-semibold text-slate-500 truncate">
                          {(() => {
                            const matchedProg = (appCtx?.programsList || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
                            const stages = getPipelineStages(matchedProg);
                            return stageLabel(stages, app.masterStage, 'Phase 0');
                          })()}
                        </p>
                      </div>}
                    </button>
                  </div>
                ))}
              </div>
              {!applicantListCollapsed && (appCtx.totalPages || 0) > 1 && <div className="px-4 py-3 border-t border-slate-200 shrink-0 flex items-center justify-between gap-2">
                <button aria-label="Previous page" title="Previous page" disabled={appCtx.currentPage <= 1} onClick={() => appCtx.setCurrentPage(Math.max(1, (appCtx.currentPage || 1) - 1))} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
                <p className="text-caption text-slate-400">Page {appCtx.currentPage || 1} of {appCtx.totalPages}</p>
                <button aria-label="Next page" title="Next page" disabled={appCtx.currentPage >= appCtx.totalPages} onClick={() => appCtx.setCurrentPage(Math.min(appCtx.totalPages, (appCtx.currentPage || 1) + 1))} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
              </div>}
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50">
              {(() => {
                const activeApp = appCtx.selectedApp;
                const matchedProg = activeApp ? programs.find(p => p.id === activeApp.programId || p.name === activeApp.programType) : null;
                const pipelineStages = getPipelineStages(matchedProg);

                return (
                  <div className="px-4 py-2.5 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs flex items-center gap-3 min-h-[48px] overflow-x-auto">
                    <button
                      onClick={() => {
                        appCtx.setSelectedApp(null);
                        navigate('/applications');
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200/80 text-slate-700 font-extrabold text-xs transition-all cursor-pointer border border-slate-200/60 shadow-2xs shrink-0"
                    >
                      <ChevronLeft className="h-4 w-4" /> Back
                    </button>
                    {appCtx.applicantReturnOrigin && applicantOriginLabel && (
                      <>
                        <span className="h-5 w-px bg-slate-200/80 shrink-0" />
                        <button
                          onClick={handleReturnFromApplicant}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl btn-primary text-white font-extrabold text-xs transition-all cursor-pointer shadow-2xs shrink-0"
                          title={`Return to ${applicantOriginLabel}`}
                        >
                          <RotateCcw className="h-4 w-4" /> Back to {applicantOriginLabel}
                        </button>
                      </>
                    )}
                    {activeApp && (
                      <>
                        <span className="h-5 w-px bg-slate-200/80 shrink-0" />
                        <div className="flex items-center gap-2.5 shrink-0">
                          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-50 to-slate-100 overflow-hidden ring-1 ring-slate-200/80 shrink-0 shadow-2xs flex items-center justify-center">
                            {activeApp.profilePhotoUrl
                              ? <img src={activeApp.profilePhotoUrl} className="h-full w-full object-cover" alt="" />
                              : <span className="text-[10px] font-extrabold text-blue-600">
                                  {activeApp.personalDetails?.firstName?.charAt(0) || activeApp.userEmail?.charAt(0).toUpperCase() || '?'}
                                </span>
                            }
                          </div>
                          <div className="leading-tight hidden sm:block">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-bold text-slate-900 leading-tight">
                                {activeApp.personalDetails?.firstName || '—'} {activeApp.personalDetails?.lastName || ''}
                              </p>
                              {activeApp.programType && (
                                <span className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 font-extrabold text-[11px] border border-blue-100/80">
                                  {activeApp.programType}
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] font-semibold text-slate-400 font-mono mt-0.5">{activeApp.applicationId}</p>
                          </div>
                        </div>
                        <span className="h-5 w-px bg-slate-200/80 shrink-0 hidden sm:block" />
                        <div className="flex items-center gap-1.5 flex-1 overflow-x-auto py-0.5 scrollbar-none">
                          {pipelineStages.map((stage, i) => {
                            const current = typeof activeApp.masterStage === 'number' ? activeApp.masterStage : 0;
                            const isActive = i === current;
                            const isPast = i < current;
                            const isClickable = activeApp.active !== false;

                            return (
                              <button
                                key={stage.id}
                                onClick={() => isClickable && appCtx.handleMasterStageChange?.(i)}
                                disabled={!isClickable}
                                title={`${stage.label}${stage.subtitle ? ` — ${stage.subtitle}` : ''}${!isClickable ? ' (inactive)' : ''}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-caption font-extrabold transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0 border ${
                                  isActive
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-500/20'
                                    : isPast
                                    ? 'bg-emerald-50/80 text-emerald-700 border-emerald-200/60 hover:bg-emerald-100/80'
                                    : 'bg-slate-50/80 text-slate-500 border-slate-200/60 hover:bg-slate-100/80 hover:text-slate-800'
                                }`}
                              >
                                {isPast ? <Check className="h-3 w-3 stroke-[2.5]" /> : null}
                                <span>{stage.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              <ApplicantDetail selectedApp={appCtx.selectedApp} appCtx={appCtx} events={eventsCtx.events} eventsCtx={eventsCtx} adminEmail={adminEmail} batchesCtx={batchesCtx}
                currentUserProfile={profileData}
                allAdmins={usersCtx.allUsers?.filter((u: any) => {
                  const r = (u.workspaceRole || u.role || '').toLowerCase();
                  return r && !['applicant', 'job_seeker', 'candidate'].includes(r);
                }).map((u: any) => {
                  const displayName = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown';
                  return { uid: u.uid, name: displayName, email: u.email || '' };
                })}
              />
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-6 space-y-8 animate-fade-in">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-6">
              <KpiCard label="Total Applicants" value={appCtx.stats.total} icon={Users} isHero={true} />
              <KpiCard label="In Progress" value={appCtx.stats.inProgress} icon={Sparkles} />
              <KpiCard label="Submitted" value={appCtx.stats.submitted} icon={CheckCircle2} />
              <KpiCard label="Returned" value={appCtx.stats.returned} icon={RotateCcw} />
              <KpiCard label="Cancelled" value={appCtx.stats.cancelled} icon={X} />
            </div>

            {/* ── APPLICANTS NAVIGATION: ARCHIVED vs DRILL-DOWN vs PROGRAM HUB ── */}
            {applicantSubView === 'archived' ? (
              <ArchivedApplications
                applications={appCtx.applications}
                onRestoreApplicant={async (appId) => {
                  await appCtx.handleToggleApplicationActive(appId);
                }}
                onInspectApplicant={(app) => {
                  appCtx.viewApplicantInWorkspace(app, { tab: 'applicants', subView: 'archived' });
                }}
                onBackToActive={() => {
                  setApplicantSubView('hub');
                  updateUrlQueryParams({ view: null });
                }}
              />
            ) : ((appCtx.programFilter && appCtx.programFilter.length > 0) || appCtx.applicantViewMode === 'table') ? (
              <div className="space-y-4">
                {/* Level 1 (All Applicants) / Level 2 (Program Drill-Down) Header Controls */}
                <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {appCtx.programFilter && appCtx.programFilter.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <button
                          type="button"
                          onClick={() => {
                            appCtx.setProgramFilter([]);
                            appCtx.setMasterStageFilter([]);
                            updateUrlQueryParams({ program: null });
                          }}
                          className="text-slate-400 hover:text-slate-700 transition cursor-pointer"
                        >
                          Applicants
                        </button>
                        <span className="text-slate-300">&gt;</span>
                        <span className="text-slate-900 font-extrabold">
                          {(appCtx.programsList || []).find((p: any) => p.id === appCtx.programFilter[0] || p.programId === appCtx.programFilter[0])?.name || appCtx.programFilter.join(", ")}
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-brand text-[11px] font-black border border-blue-100">
                          {appCtx.filteredApps.length} Active Applicants
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            appCtx.setProgramFilter([]);
                            appCtx.setMasterStageFilter([]);
                            updateUrlQueryParams({ program: null });
                          }}
                          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 transition-all cursor-pointer flex items-center gap-1.5"
                        >
                          <span>&larr; Exit Program</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-xs font-bold text-slate-600">
                        <h1 className="text-lg font-black text-slate-900 tracking-tight">All Applicants</h1>
                        <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-brand text-[11px] font-black border border-blue-100">
                          {appCtx.filteredApps.length} Active Applicants
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          appCtx.setApplicantViewMode('card');
                          updateUrlQueryParams({ program: null });
                        }}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <LayoutDashboard className="h-3.5 w-3.5" /><span>View Program Hub</span>
                      </button>
                    </>
                  )}
                </div>
              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  Applicants ({appCtx.filteredApps.length})
                </h3>
                <div className="flex items-center space-x-1 border border-slate-200 bg-slate-50 p-1 rounded-xl shrink-0">
                  <button
                    onClick={() => appCtx.setApplicantViewMode('table')}
                    className={`px-3 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
                      (appCtx.applicantViewMode || 'table') === 'table'
                        ? 'bg-white text-brand shadow-sm'
                        : 'text-slate-600 hover:text-slate-950'
                    }`}
                  >
                    <List className="h-3.5 w-3.5" /><span>Table</span>
                  </button>
                  <button
                    onClick={() => appCtx.setApplicantViewMode('card')}
                    className={`px-3 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
                      appCtx.applicantViewMode === 'card'
                        ? 'bg-white text-brand shadow-sm'
                        : 'text-slate-600 hover:text-slate-950'
                    }`}
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" /><span>Cards</span>
                  </button>
                  {appCtx.applicantViewMode === 'table' && (
                    <div ref={columnPickerWrapperRef} className="relative">
                      <button
                        onClick={() => setShowColumnPicker(!showColumnPicker)}
                        className={`px-3 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
                          showColumnPicker ? 'bg-blue-100 text-brand' : 'text-slate-600 hover:text-slate-950'
                        }`}
                      >
                        <Eye className="h-3.5 w-3.5" /><span>Columns</span>
                      </button>
                      {showColumnPicker && (
                        <div className="absolute right-0 top-8 z-[8500] bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-48">
                          {ALL_TABLE_COLUMNS.map(col => (
                            <label key={col.id} className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-slate-50 rounded px-1">
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(col.id)}
                                onChange={() => {
                                  setVisibleColumns(prev =>
                                    prev.includes(col.id) ? prev.filter(c => c !== col.id) : [...prev, col.id]
                                  );
                                }}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                              />
                              <span className="text-xs font-semibold text-slate-700">{col.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── FILTER BAR ── */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <div className="flex flex-col lg:flex-row gap-3 items-stretch justify-between">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      value={appCtx.searchTerm || ''}
                      onChange={(e) => appCtx.setSearchTerm(e.target.value)}
                      placeholder="Search by name, email, or ID..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium text-slate-800 transition-all"
                    />
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    {/* Create + CSV import are mutations — View Only must not see them. */}
                    {moduleLevel.applicantsEdit && (
                      <>
                        <button
                          onClick={() => appCtx.setIsCreatingCandidate(true)}
                          className="px-4 py-2 btn-primary  text-white text-caption font-semibold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
                        >
                          <Plus className="h-3.5 w-3.5" /><span>New Applicant</span>
                        </button>
                        <button
                          onClick={handleExportCsv}
                          className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-caption font-semibold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
                          title="Export applicants to CSV"
                        >
                          <Download className="h-3.5 w-3.5" /><span>Export CSV</span>
                        </button>
                        <button
                          onClick={() => setIsCsvImportOpen(true)}
                          className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-caption font-semibold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer"
                          title="Import applicants from CSV"
                        >
                          <Upload className="h-3.5 w-3.5" /><span>Import CSV</span>
                        </button>
                      </>
                    )}
                    <button aria-label="Refresh applicants" title="Refresh applicants" onClick={() => appCtx.fetchApplications()} className="p-2 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="pt-2">
                  <UnifiedApplicantFilterBar
                    filters={appCtx.unifiedFilters}
                    onFilterChange={appCtx.setUnifiedFilters}
                    includeMasterPhase={false}
                    lockedProgramId={appCtx.programFilter?.length ? appCtx.programFilter[0] : undefined}
                    lockedProgramName={(() => {
                      if (!appCtx.programFilter?.length) return undefined;
                      const prog = (appCtx.programsList || []).find((p: any) => p.id === appCtx.programFilter[0] || p.programId === appCtx.programFilter[0]);
                      const code = prog?.code || prog?.shortCode || prog?.programCode;
                      return prog ? (code ? `${code} — ${prog.name || prog.id}` : (prog.name || prog.id)) : appCtx.programFilter[0];
                    })()}
                    programsList={appCtx.programsList}
                    batchesList={batchesCtx.batches}
                    sponsorsList={appCtx.providers || []}
                    teamMembersList={usersCtx.allUsers}
                    applications={appCtx.applications}
                  />
                </div>
              </div>

              {/* ── TABLE VIEW ── */}
              {(appCtx.applicantViewMode || 'table') === 'table' ? (
                <div className="table-container">
                  {appCtx.loading ? (
                    <div className="flex items-center justify-center py-16">
                      <span className="animate-spin h-8 w-8 border-2 border-slate-200 border-t-blue-600 rounded-full" />
                    </div>
                  ) : appCtx.displayedApps?.length > 0 ? (
                    <div className="overflow-x-auto -mx-6 px-6">
                      <table className="min-w-full text-left border-collapse">
                        <thead>
                          <tr>
                            {moduleLevel.applicantsEdit && (
                              <th className="py-3 px-4 w-8">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                                  checked={displayedSelectionShare === 'all'}
                                  ref={(el) => {
                                    if (el) el.indeterminate = displayedSelectionShare === 'some';
                                  }}
                                  onChange={toggleDisplayedPageSelection}
                                />
                              </th>
                            )}
                            {ALL_TABLE_COLUMNS.filter(c => visibleColumns.includes(c.id)).map((col) => (
                              <th
                                key={col.id}
                                onClick={() => handleSort(col.id)}
                                className="py-3 px-4 whitespace-nowrap cursor-pointer hover:bg-slate-100/70 select-none"
                              >
                                <div className="inline-flex items-center gap-1">
                                  <span>{col.label}</span>
                                  {sortColumn === col.id && (
                                    <span className="text-brand font-bold">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                            ))}
                            <th className="py-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedDisplayedApps.map((app: any) => {
                            return (
                              <tr
                                key={app.applicationId}
                                className="cursor-pointer"
                                onClick={() => appCtx.handleSelectApplicant(app)}
                              >
                                {moduleLevel.applicantsEdit && (
                                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={(appCtx.selectedAppIds || []).includes(app.applicationId)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          appCtx.setSelectedAppIds([...(appCtx.selectedAppIds || []), app.applicationId]);
                                        } else {
                                          appCtx.setSelectedAppIds((appCtx.selectedAppIds || []).filter((id: string) => id !== app.applicationId));
                                        }
                                      }}
                                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                                    />
                                  </td>
                                )}
                                {ALL_TABLE_COLUMNS.filter(c => visibleColumns.includes(c.id)).map((col) => (
                                  <td key={col.id} className="py-3.5 px-4 text-xs">{col.render(app)}</td>
                                ))}
                                <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center space-x-1 justify-end">
                                    {moduleLevel.applicantsEdit && getSubStatus(app) === 'Submitted' && (
                                      <button
                                        onClick={async () => {
                                          if (!(await confirmDialog(`Return "${app.personalDetails?.firstName || app.userEmail}" application to applicant? They can edit and resubmit.`))) return;
                                          await appCtx.handleUpdateStatus(app.applicationId, ApplicationStatus.RETURNED);
                                        }}
                                        className="p-1 px-2 border border-amber-200 hover:bg-amber-50 hover:border-amber-300 text-amber-700 rounded-lg transition-all font-semibold cursor-pointer text-xs flex items-center space-x-1"
                                        title="Return to applicant"
                                      >
                                        <RotateCcw className="h-3 w-3" /><span>Return</span>
                                      </button>
                                    )}
                                    {moduleLevel.applicantsEdit && (getSubStatus(app) === 'Draft' || getSubStatus(app) === 'Returned') && (
                                      <button
                                        onClick={async () => {
                                          if (!(await confirmDialog(`Submit "${app.personalDetails?.firstName || app.userEmail}" application on their behalf?`))) return;
                                          await appCtx.handleUpdateStatus(app.applicationId, ApplicationStatus.SUBMITTED);
                                        }}
                                        className="p-1 px-2 border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 rounded-lg transition-all font-semibold cursor-pointer text-xs flex items-center space-x-1"
                                        title="Submit on behalf of applicant"
                                      >
                                        <Send className="h-3 w-3" /><span>Submit</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => appCtx.handleSelectApplicant(app)}
                                      className="p-1 px-2 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-600 rounded-lg transition-all flex items-center space-x-1 font-semibold cursor-pointer text-xs"
                                    >
                                      <Eye className="h-3 w-3" /><span>View</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-body font-semibold text-slate-500">No applicants found</p>
                      <p className="text-caption text-slate-400 mt-1">Try adjusting your search or filters.</p>
                    </div>
                  )}

                  {/* Pagination */}
                  {(appCtx.totalPages || 0) > 1 && (
                    <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <p className="text-caption text-slate-400">Page {appCtx.currentPage || 1} of {appCtx.totalPages}</p>
                        <select
                          value={appCtx.itemsPerPage || 25}
                          onChange={(e) => { appCtx.setItemsPerPage(Number(e.target.value)); }}
                          className="text-caption font-semibold border border-slate-200 rounded-lg px-2 py-1 bg-white cursor-pointer outline-none "
                        >
                          <option value={25}>25 / page</option>
                          <option value={50}>50 / page</option>
                          <option value={100}>100 / page</option>
                          <option value={150}>150 / page</option>
                          <option value={200}>200 / page</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <button
                          disabled={appCtx.currentPage <= 1}
                          onClick={() => appCtx.setCurrentPage(Math.max(1, (appCtx.currentPage || 1) - 1))}
                          className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                        >
                          <ChevronLeft className="h-3 w-3" /><span>Prev</span>
                        </button>
                        <button
                          disabled={appCtx.currentPage >= appCtx.totalPages}
                          onClick={() => appCtx.setCurrentPage(Math.min(appCtx.totalPages, (appCtx.currentPage || 1) + 1))}
                          className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                        >
                          <span>Next</span><ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              ) : (
                /* ── CARD VIEW ── */
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                  {appCtx.loading ? (
                    <div className="flex items-center justify-center py-16">
                      <span className="animate-spin h-8 w-8 border-2 border-slate-200 border-t-blue-600 rounded-full" />
                    </div>
                  ) : appCtx.displayedApps && appCtx.displayedApps.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {appCtx.displayedApps.map((app: any) => (
                        <div key={app.applicationId} className="p-4 hover:bg-slate-50 transition-all flex items-center justify-between">
                          <div className="flex items-center space-x-3 min-w-0">
                            {moduleLevel.applicantsEdit && (
                              <input
                                type="checkbox"
                                checked={(appCtx.selectedAppIds || []).includes(app.applicationId)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    appCtx.setSelectedAppIds([...(appCtx.selectedAppIds || []), app.applicationId]);
                                  } else {
                                    appCtx.setSelectedAppIds((appCtx.selectedAppIds || []).filter((id: string) => id !== app.applicationId));
                                  }
                                }}
                                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            <div className="flex items-center space-x-4 min-w-0 cursor-pointer" onClick={() => appCtx.handleSelectApplicant(app)}>
                              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-brand shrink-0">
                                {app.personalDetails?.firstName?.charAt(0) || app.userEmail?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900 truncate">{app.personalDetails?.firstName || '—'} {app.personalDetails?.lastName || ''}</p>
                                <p className="text-caption text-slate-400 truncate">{app.userEmail}</p>
                                {((app as any).tags || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {(app as any).tags.slice(0, 3).map((tag: string) => (
                                      <span key={tag} className="px-1.5 py-0.5 text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-100 rounded-md">{tag}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 shrink-0">
                            <span className="inline-flex items-center gap-1.5">
                              <span className={`h-2.5 w-2.5 rounded-full ${(app as any)?.activeStatus !== false ? "bg-emerald-500" : "bg-slate-300"}`} title={(app as any)?.activeStatus !== false ? "Active" : "Inactive"} />
                              <span className="text-[11px] font-semibold text-slate-500">{(app as any)?.activeStatus !== false ? "Active" : "Inactive"}</span>
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-semibold ${
                              getSubStatus(app) === 'Submitted' ? 'bg-brand-active border border-blue-100' :
                              getSubStatus(app) === 'Draft' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                              getSubStatus(app) === 'Returned' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
                              getSubStatus(app) === 'Cancelled' ? 'bg-red-50 text-red-700 border border-red-100' :
                              'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}>{getSubStatus(app)}</span>
                            {moduleLevel.applicantsEdit && getSubStatus(app) === 'Submitted' && (
                              <button
                                onClick={async (e) => { e.stopPropagation(); if (!(await confirmDialog(`Return "${app.personalDetails?.firstName || app.userEmail}" application?`))) return; await appCtx.handleUpdateStatus(app.applicationId, ApplicationStatus.RETURNED); }}
                                className="p-1.5 border border-amber-200 hover:bg-amber-50 text-amber-700 rounded-lg transition-all cursor-pointer"
                                title="Return to applicant"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </button>
                            )}
                            {moduleLevel.applicantsEdit && (getSubStatus(app) === 'Draft' || getSubStatus(app) === 'Returned') && (
                              <button
                                onClick={async (e) => { e.stopPropagation(); if (!(await confirmDialog(`Submit "${app.personalDetails?.firstName || app.userEmail}" application on their behalf?`))) return; await appCtx.handleUpdateStatus(app.applicationId, ApplicationStatus.SUBMITTED); }}
                                className="p-1.5 border border-blue-200 bg-brand-active text-brand rounded-lg transition-all cursor-pointer"
                                title="Submit on behalf of applicant"
                              >
                                <Send className="h-3 w-3" />
                              </button>
                            )}
                            <svg className="h-4 w-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <User className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-body font-semibold text-slate-500">No applicants found</p>
                    </div>
                  )}
                  {/* Pagination (card view) */}
                  {(appCtx.totalPages || 0) > 1 && (
                    <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <p className="text-caption text-slate-400">Page {appCtx.currentPage || 1} of {appCtx.totalPages}</p>
                        <select
                          value={appCtx.itemsPerPage || 25}
                          onChange={(e) => { appCtx.setItemsPerPage(Number(e.target.value)); }}
                          className="text-caption font-semibold border border-slate-200 rounded-lg px-2 py-1 bg-white cursor-pointer outline-none "
                        >
                          <option value={25}>25 / page</option>
                          <option value={50}>50 / page</option>
                          <option value={100}>100 / page</option>
                          <option value={150}>150 / page</option>
                          <option value={200}>200 / page</option>
                        </select>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <button
                          disabled={appCtx.currentPage <= 1}
                          onClick={() => appCtx.setCurrentPage(Math.max(1, (appCtx.currentPage || 1) - 1))}
                          className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                        >
                          <ChevronLeft className="h-3 w-3" /><span>Prev</span>
                        </button>
                        <button
                          disabled={appCtx.currentPage >= appCtx.totalPages}
                          onClick={() => appCtx.setCurrentPage(Math.min(appCtx.totalPages, (appCtx.currentPage || 1) + 1))}
                          className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center space-x-1 cursor-pointer"
                        >
                          <span>Next</span><ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Light Surface Responsive Bulk Action Floating Toolbar */}
            {moduleLevel.applicantsEdit && (appCtx.selectedAppIds || []).length > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[8500] bg-white/95 backdrop-blur-md border border-slate-200 text-slate-900 rounded-2xl shadow-2xl px-4 py-3 flex flex-wrap items-center gap-2.5 max-w-[95vw] sm:max-w-[90vw] md:max-w-4xl animate-fade-in">
                {/* Count Badge */}
                <span className="text-xs font-black px-2.5 py-1 bg-brand rounded-lg text-white whitespace-nowrap shrink-0">
                  {appCtx.selectedAppIds.length} Selected
                </span>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Select All in Current View */}
                <button
                  onClick={() => {
                    const currentIds = (appCtx.displayedApps || []).map((a: any) => a.applicationId).filter(Boolean);
                    appCtx.setSelectedAppIds([...(appCtx.selectedAppIds || []), ...currentIds.filter((id: string) => !(appCtx.selectedAppIds || []).includes(id))]);
                  }}
                  className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Select All in View
                </button>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Status Actions Group */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => appCtx.handleBulkStatusChange(ApplicationStatus.DRAFT)} className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap">Draft</button>
                  <button onClick={() => appCtx.handleBulkStatusChange(ApplicationStatus.SUBMITTED)} className="px-2.5 py-1.5 bg-brand hover:bg-blue-700 text-white text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap">Submit</button>
                  <button onClick={() => appCtx.handleBulkStatusChange(ApplicationStatus.RETURNED)} className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap">Return</button>
                  <button onClick={() => appCtx.handleBulkStatusChange(ApplicationStatus.CANCELLED)} className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap">Cancel</button>
                </div>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Direct Email Action */}
                <button
                  onClick={() => setShowBulkEmailModal(true)}
                  className="px-3 py-1.5 bg-brand hover:bg-blue-700 text-white text-caption font-semibold rounded-xl transition-all cursor-pointer flex items-center gap-1 whitespace-nowrap shadow-2xs"
                >
                  <Mail className="h-3.5 w-3.5" /> Email Selected
                </button>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Quick Actions: Tags & Assign */}
                <button
                  onClick={async () => {
                    const raw = await promptDialog(`Enter tags for ${appCtx.selectedAppIds.length} selected applicant(s), separated by commas:`);
                    if (raw === null) return;
                    const tags = raw.split(',').map(t => t.trim()).filter(Boolean);
                    appCtx.handleBulkTagUpdate(tags);
                  }}
                  className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-caption font-semibold rounded-xl transition-all cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <Tag className="h-3.5 w-3.5 text-slate-500" /> Tags
                </button>

                <button
                  onClick={() => setShowTeamAssignModal(true)}
                  className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-caption font-semibold rounded-xl transition-all cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <UserCheck className="h-3.5 w-3.5 text-slate-500" /> Assign
                </button>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Grouped Operations Popover for Selects & Operations */}
                <div ref={moreBulkActionsRef} className="relative">
                  <button
                    onClick={() => setShowMoreBulkActions(!showMoreBulkActions)}
                    className={`px-3 py-1.5 text-caption font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 border ${
                      showMoreBulkActions
                        ? 'bg-blue-50 border-brand text-brand'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    <span>Operations</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${showMoreBulkActions ? 'rotate-180' : ''}`} />
                  </button>

                  {showMoreBulkActions && (
                    <div className="absolute right-0 bottom-11 z-[var(--z-dropdown)] bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 w-72 sm:w-80 space-y-3.5 animate-fade-in">
                      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                        <span className="text-xs font-bold text-slate-800">Bulk Operations</span>
                        <button aria-label="Close" title="Close" onClick={() => setShowMoreBulkActions(false)} className="text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer">✕</button>
                      </div>

                      {/* Add to Batch */}
                      <div className="space-y-1">
                        <label className="text-caption font-bold text-slate-500 block">Add to Batch</label>
                        <SearchableSelect
                          value=""
                          onChange={async (v) => {
                            if (v) {
                              const selectedBatch = batchesCtx.batches.find((b: any) => b.batchId === v);
                              if (selectedBatch && (await confirmDialog(`Add ${appCtx.selectedAppIds.length} selected applicant(s) to batch "${selectedBatch.name}"?`))) {
                                const currentMemberIds = selectedBatch.applicantIds || [];
                                const newMemberIds = [...new Set([...currentMemberIds, ...(appCtx.selectedAppIds || [])])];
                                batchesCtx.handleUpdateBatchMembers(selectedBatch.batchId, newMemberIds, `Bulk added ${appCtx.selectedAppIds.length} applicants from applicant list`);
                                setShowMoreBulkActions(false);
                              }
                            }
                          }}
                          options={batchesCtx.batches.filter((b: any) => b.status !== 'Completed').map((b: any) => ({
                            value: b.batchId,
                            label: `${b.name} (${(b.applicantIds || []).length})`,
                          }))}
                          placeholder="Select Batch..."
                          allowClear={false}
                          emptyLabel="No batches found"
                          dropdownPosition="top"
                        />
                      </div>

                      {/* Change Program */}
                      <div className="space-y-1">
                        <label className="text-caption font-bold text-slate-500 block">Change Program</label>
                        <SearchableSelect
                          value=""
                          onChange={(v) => {
                            if (v) {
                              appCtx.handleBulkProgramUpdate(v);
                              setShowMoreBulkActions(false);
                            }
                          }}
                          options={(appCtx.programsList || []).map((p: any) => {
                            const code = p.code || p.shortCode || p.programCode;
                            return {
                              value: p.id || p.programId,
                              label: code ? `${code} — ${p.name || p.id}` : (p.name || p.programId || p.id),
                            };
                          })}
                          placeholder="Select Program..."
                          allowClear={false}
                          emptyLabel="No programs found"
                          dropdownPosition="top"
                        />
                      </div>

                      {/* Change Partner */}
                      <div className="space-y-1">
                        <label className="text-caption font-bold text-slate-500 block">Change Partner</label>
                        <SearchableSelect
                          value=""
                          onChange={(v) => {
                            if (v) {
                              appCtx.handleBulkPartnerChange(v);
                              setShowMoreBulkActions(false);
                            }
                          }}
                          options={(appCtx.providers || []).filter((p: any) => p.active !== false).map((p: any) => ({
                            value: p.providerId || p.id,
                            label: p.name || p.providerId || p.id,
                          }))}
                          placeholder="Select Partner..."
                          allowClear={false}
                          emptyLabel="No partners found"
                          dropdownPosition="top"
                        />
                      </div>

                      {/* Change Phase (if program filter is active) */}
                      {appCtx.programFilter && appCtx.programFilter.length > 0 && (
                        <div className="space-y-1">
                          <label className="text-caption font-bold text-slate-500 block">Change Phase</label>
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value !== '') {
                                appCtx.handleBulkMasterStageChange(Number(e.target.value));
                                setShowMoreBulkActions(false);
                              }
                            }}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 text-slate-800 text-caption font-semibold rounded-xl appearance-none outline-none focus:bg-white focus:border-brand cursor-pointer"
                          >
                            <option value="" disabled>Select Target Phase...</option>
                            {(() => {
                              const activeProgId = appCtx.programFilter[0];
                              const allProgs = [...(programs || []), ...(appCtx.programsList || [])];
                              const activeProg = allProgs.find((p: any) => p.id === activeProgId || p.programId === activeProgId || p.name === activeProgId || p.code === activeProgId);
                              const activeStages = getPipelineStages(activeProg);
                              return activeStages.map((s, i) => (
                                <option key={s.id || i} value={i}>{s.label}</option>
                              ));
                            })()}
                          </select>
                        </div>
                      )}

                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                        {/* Archive */}
                        <button
                          onClick={() => {
                            setShowMoreBulkActions(false);
                            appCtx.handleBulkArchive();
                          }}
                          className="flex-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-caption font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1"
                        >
                          <Archive className="h-3.5 w-3.5 text-slate-500" /> Archive
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => {
                            setShowMoreBulkActions(false);
                            appCtx.handleBulkDelete();
                          }}
                          className="flex-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-caption font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-600" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-4 w-px bg-slate-200 shrink-0 hidden sm:block" />

                {/* Clear Selection */}
                <button
                  onClick={() => {
                    appCtx.setSelectedAppIds([]);
                    setShowMoreBulkActions(false);
                  }}
                  className="px-2.5 py-1.5 bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-600 text-caption font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Clear
                </button>
              </div>
            )}
            </div>
            ) : (
              <ProgramCardHub
                programs={appCtx.programsList || []}
                applications={appCtx.applications || []}
                onSelectProgram={(programId) => {
                  appCtx.setProgramFilter([programId]);
                  appCtx.setApplicantViewMode('table');
                  updateUrlQueryParams({ program: programId });
                }}
                onOpenArchived={() => {
                  setApplicantSubView('archived');
                  updateUrlQueryParams({ view: 'archived' });
                }}
                archivedCount={(appCtx.applications || []).filter(a => !isApplicationActive(a)).length}
                onCreateCandidate={moduleLevel.applicantsEdit ? () => appCtx.setIsCreatingCandidate(true) : undefined}
                onTableView={() => appCtx.setApplicantViewMode('table')}
              />
            )}

            {/* Application Wizard (multi-step) */}
            {appCtx.isCreatingCandidate && (
              <ApplicationWizard
                programsList={appCtx.programsList || []}
                onComplete={handleWizardComplete}
                onClose={() => appCtx.setIsCreatingCandidate(false)}
                mode="admin"
              />
            )}
          </div>
        )}
      </main>

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <Modal onClose={() => setShowBulkEmailModal(false)} ariaLabel="Email Selected Applicants">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-xl w-full mx-4 space-y-4 font-sans text-slate-800">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Email Selected Applicants</h3>
                  <p className="text-caption text-slate-500 mt-0.5">
                    Targeting {appCtx.applications.filter((a: any) => (appCtx.selectedAppIds || []).includes(a.applicationId) && a.userEmail).length} candidate(s)
                  </p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setShowBulkEmailModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subject Line *</label>
                <input
                  type="text"
                  value={bulkEmailSubject}
                  onChange={e => setBulkEmailSubject(e.target.value)}
                  placeholder="e.g. Important Update regarding your Application"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Message Body *</label>
                <textarea
                  rows={6}
                  value={bulkEmailBody}
                  onChange={e => setBulkEmailBody(e.target.value)}
                  placeholder="Type your message here..."
                  className="w-full border border-slate-200 rounded-xl p-3 text-xs outline-none focus:border-blue-500 font-medium"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() => setShowBulkEmailModal(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-xl hover:bg-slate-50 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBulkEmailModal}
                disabled={bulkEmailSending || !bulkEmailSubject.trim() || !bulkEmailBody.trim()}
                className="px-5 py-2 btn-primary text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                {bulkEmailSending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {bulkEmailSending ? 'Queuing Job...' : 'Send Bulk Email'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Archive Confirmation Modal */}
      {appCtx.showArchiveModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 animate-fade-in" style={{ zIndex: archiveZ }} onClick={() => { if (!appCtx.archiving) appCtx.setShowArchiveModal(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-rose-600" />
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Archive Applications</h3>
                  <p className="text-caption text-slate-400 mt-0.5">{(appCtx.selectedAppIds || []).length} application(s) selected</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { if (!appCtx.archiving) appCtx.setShowArchiveModal(false); }} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer" disabled={appCtx.archiving}>
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Are you sure you want to archive {(appCtx.selectedAppIds || []).length} application(s)?</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Archived applications will be hidden from all users and will not appear in any listings. They will remain stored in the backend for record-keeping.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button
                onClick={() => { appCtx.setShowArchiveModal(false); }}
                className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer"
                disabled={appCtx.archiving}
              >
                Cancel
              </button>
              <button
                onClick={appCtx.handleArchiveSelected}
                disabled={appCtx.archiving}
                className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-2"
              >
                {appCtx.archiving ? (
                  <>Archiving...</>
                ) : (
                  <><Archive className="h-4 w-4" /> Archive {(appCtx.selectedAppIds || []).length}</>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onImportComplete={() => {
          localStorage.removeItem('all_cached_applications');
          localStorage.removeItem('all_cached_applications_at');
          appCtx.fetchApplications();
        }}
        programs={appCtx.programsList}
      />

      {/* Bulk Assign to Team Member(s) Modal */}
      <WorkspaceTeamAssignModal
        open={showTeamAssignModal}
        applicationIds={appCtx.selectedAppIds || []}
        onClose={() => setShowTeamAssignModal(false)}
        onAssigned={() => {
          appCtx.fetchApplications({ force: true });
          appCtx.setSelectedAppIds([]);
        }}
      />

      {/* Document Preview Modal */}
      {activePreviewDocument && (
        <DocumentPreviewModal
          document={activePreviewDocument}
          onClose={() => setActivePreviewDocument(null)}
          onOpenApplication={(appId) => {
            const targetApp = appCtx.applications.find(a => a.id === appId || a.applicationId === appId);
            // ADM-13: the jump closes this document modal; Back in the
            // workspace returns to the Documents Centre (see PR notes).
            if (targetApp) {
              setActivePreviewDocument(null);
              appCtx.viewApplicantInWorkspace(targetApp, { tab: 'documents' });
            }
          }}
          onRefresh={() => {
            // A modal write (decision / override / retry) can change
            // what the Documents Centre list shows — bump the refresh
            // signal so a mounted centre silently refetches. When the
            // decision came from another surface, the centre simply
            // loads fresh data on its next mount, as before.
            setDocumentsRefreshSignal(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
}
